import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Clock, ListChecks } from 'lucide-react';
import { api, ErrorApi } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import Modal from '../ui/Modal';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { etiquetaCampo } from '../experiencia/etiquetas';
import { motivoDeError } from '../ui/estados';
import { useAuth } from '../lib/contextoAuth';
import Motivo from '../experiencia/Motivo';
import AvisoNoSeEjecutaSola from '../accesos/AvisoNoSeEjecutaSola';
import { PERMISO_REGLAS } from '../accesos/umbral';

/* =====================================================================
   AUTORIZACIONES · Accesos Fase 3
   =====================================================================
   DOS PESTAÑAS Y NADA MAS, porque son las dos preguntas que la gente se
   hace de verdad: **«¿qué me toca firmar?»** y **«¿en qué va lo que
   pedí?»**. Una sola lista con un filtro obligaría a elegir el filtro
   antes de poder contestar ninguna de las dos.

   ESTA PANTALLA CONVIVE CON «APROBACIONES», que es el cuatro ojos de los
   movimientos y sigue mandando sobre lo suyo. Aquí caen las acciones que
   aquella no cubre. La nota de arriba lo dice para que nadie se quede
   mirando la pestaña vacía preguntándose dónde está su solicitud.

   SIN VERDE DECORATIVO. Ámbar = esperando, gris = resuelta, rojo =
   falló. El único verde es «ejecutada», que es un hecho comprobado: el
   inventario cambió y la solicitud dice qué movimiento salió.
   ===================================================================== */
const ESTADO_SOLICITUD = {
  PENDIENTE: { etiqueta: 'Esperando firma', color: 'ambar' },
  APROBADA: { etiqueta: 'Autorizada', color: 'ambar' },
  EJECUTADA: { etiqueta: 'Hecha', color: 'verde' },
  RECHAZADA: { etiqueta: 'Rechazada', color: 'gris' },
  CADUCADA: { etiqueta: 'Se pasó el plazo', color: 'gris' },
  CANCELADA: { etiqueta: 'Retirada', color: 'gris' },
  FALLIDA: { etiqueta: 'Falló al aplicarse', color: 'rojo' },
};

// LO QUE DICE EL BOTÓN DE CADA RENGLÓN. En «Para mí» todo es firmable (el
// servidor solo manda las pendientes que esta persona puede decidir), así
// que abre la ventana con Autorizar y Rechazar. En «Mías» quien mira es
// quien pidió y nunca firma lo suyo: la misma ventana le enseña el estado,
// las firmas y quién decidió, sin botones de decidir. Por eso «Ver».
const ABRIR_PARA_MI = 'Ver y decidir';
const ABRIR_MIAS = 'Ver';

export default function Autorizaciones({ irA }) {
  const { puede } = useAuth();
  // EX-5 (1 oct 2026): la nota de abajo nombra «Reglas de autorización» y
  // ahora esa pantalla existe. Quien no tiene el permiso ve el botón gris
  // con el motivo, no una puerta que da a un 403.
  const puedeReglas = typeof puede === 'function' ? puede(PERMISO_REGLAS) : false;
  const [cual, setCual] = useState('para-mi');
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [abierta, setAbierta] = useState(null);

  // EL EFECTO NO LLAMA A `cargar`, y no es capricho del lint: poner el
  // `setCargando(true)` de forma síncrona dentro del efecto encadena un
  // render de más cada vez que se cambia de pestaña. Se pide fuera y se
  // escribe el estado en el `then`, que ya es asíncrono.
  //
  // `cargar` se queda para lo que sirve: volver a pedir después de
  // decidir, donde sí se llama desde un manejador.
  const cargar = useCallback((quien) => api.get(`/autorizaciones?cual=${quien}`)
    .then((d) => { setFilas(d); setError(null); })
    .catch((e) => setError(e.message)), []);

  useEffect(() => {
    let vigente = true;
    api.get(`/autorizaciones?cual=${cual}`)
      .then((d) => { if (vigente) { setFilas(d); setError(null); } })
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [cual]);

  const paraMi = cual === 'para-mi';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Firmas pendientes</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Ajustes, bajas y cambios que necesitan la firma de una segunda
          persona antes de hacerse. Quien firma nunca es quien pidió.
        </p>
      </div>

      <NotaExplicativa titulo="¿Y las salidas y mermas que esperan aprobación?">
        Esas siguen en <strong>Salidas por aprobar</strong>, que es la pantalla
        de siempre. Aquí caen las demás acciones —ajustes de
        inventario, bajas de producto, cambios de puesto a administrador— y
        solo si tu empresa las tiene encendidas en{' '}
        <strong>Reglas de autorización</strong>. De fábrica están todas
        apagadas.
      </NotaExplicativa>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => irA?.('accesos-reglas-de-autorizacion')}
          disabled={!puedeReglas}
          aria-describedby="motivo-abrir-reglas"
          className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 disabled:opacity-50"
        >
          <ListChecks className="h-4 w-4" aria-hidden="true" /> Abrir Reglas de autorización
        </button>
        <Motivo id="motivo-abrir-reglas" cuando={!puedeReglas} className="basis-full">
          Las abre quien tiene el permiso «Cambiar las reglas de autorización».
          Se concede en Personal, sin cambiar de puesto.
        </Motivo>
      </div>

      <div role="tablist" aria-label="Qué autorizaciones ver"
           className="flex gap-2">
        {[
          { id: 'para-mi', titulo: 'Para mí', pie: 'Lo que puedo firmar' },
          { id: 'mias', titulo: 'Mías', pie: 'Lo que pedí yo' },
        ].map((op) => (
          <button
            key={op.id} type="button" role="tab"
            aria-selected={cual === op.id}
            onClick={() => setCual(op.id)}
            className={`rounded-lg border px-4 py-2 text-left ${
              cual === op.id
                ? 'border-[var(--color-marca)] bg-blue-50'
                : 'border-gray-200 hover:border-gray-400'}`}
          >
            <span className="block text-sm font-semibold text-gray-900">{op.titulo}</span>
            <span className="block text-xs text-gray-500">{op.pie}</span>
          </button>
        ))}
      </div>

      {/* Los tres estados no se pisan: el error NO se enseña junto al vacío. */}
      {error && !cargando && (
        <ErrorConReintento
          que="No se pudieron cargar las firmas"
          porque={motivoDeError(error)}
          onReintentar={() => { setError(null); setCargando(true); cargar(cual).finally(() => setCargando(false)); }}
        />
      )}
      {cargando && <Cargando filas={4} />}

      {!cargando && !error && filas.length === 0 && (
        <EstadoVacio
          icono={ShieldCheck}
          titulo={paraMi ? 'No tienes nada que firmar' : 'No has pedido ninguna autorización'}
          descripcion={paraMi
            ? 'Cuando alguien pida algo que tú puedas autorizar, aparecerá aquí. No hace falta que estés pendiente: también te llega a tu bandeja de avisos.'
            : 'Si haces un ajuste o una baja que tu empresa tenga puesta bajo autorización, aparecerá aquí mientras espera.'}
        />
      )}

      {filas.length > 0 && (
        <ListaEstandar
          titulo={paraMi ? 'Esperando tu firma' : 'Lo que pediste'}
          subtitulo={paraMi
            ? `Pulsa «${ABRIR_PARA_MI}» para ver exactamente qué se pidió antes de autorizar o rechazar.`
            : `Pulsa «${ABRIR_MIAS}» para ver en qué va. Mientras diga «Esperando firma», nada ha cambiado todavía.`}
          columnas={[
            {
              // EL BOTÓN QUE ABRE LA SOLICITUD VA EN LA PRIMERA COLUMNA, debajo
              // de lo que se pidió (corrección del 7 de octubre de 2026). Aquí
              // se pasaba `alTocar`, una prop que ListaEstandar no tiene: tocar
              // el renglón no hacía nada y desde esta pantalla no se podía
              // firmar. Va aquí y no en `acciones` por dos razones medidas en
              // el marcado: los botones de `acciones` son texto sin alto
              // mínimo (no llegan a 44 px) y caen en la última columna, que a
              // 320 px queda fuera de la vista, detrás del desplazamiento de
              // la tabla. `valor` sigue siendo el resumen, así que exportar no
              // cambia.
              etiqueta: 'Qué', valor: (s) => s.resumen, clase: 'text-gray-900',
              render: (s) => {
                const etiqueta = paraMi ? ABRIR_PARA_MI : ABRIR_MIAS;
                return (
                  <div className="flex flex-col items-start gap-1">
                    <span>{s.resumen}</span>
                    <button
                      type="button"
                      onClick={() => setAbierta(s.id)}
                      aria-label={`${etiqueta}: ${s.resumen}`}
                      className="inline-flex min-h-11 items-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-[var(--color-marca)] hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-1"
                    >
                      {etiqueta}
                    </button>
                  </div>
                );
              },
            },
            { etiqueta: 'Tipo', valor: (s) => s.nombre_accion },
            { etiqueta: 'Quién lo pidió', valor: (s) => s.solicitante },
            {
              etiqueta: 'Estado', valor: (s) => s.estado_visible,
              render: (s) => <ChipEstado mapa={ESTADO_SOLICITUD} clave={s.estado_visible} />,
            },
            {
              // CUANTO LE QUEDA, y no la fecha de caducidad. «Caduca el 4
              // de septiembre a las 18:42» obliga a hacer la cuenta;
              // «en 6 horas» es la que la gente necesita para decidir si
              // atenderla ahora.
              etiqueta: 'Plazo', tipo: 'fecha_hora', valor: (s) => s.caduca_en,
              render: (s) => (s.estado_visible === 'PENDIENTE'
                ? <span className="inline-flex items-center gap-1 text-amber-700">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    <TiempoRelativo valor={s.caduca_en} />
                  </span>
                : <span className="text-gray-400">—</span>),
            },
          ]}
          filas={filas}
          exportar={{ nombre: 'autorizaciones' }}
          claveFila={(s) => s.id}
        />
      )}

      {abierta && (
        <ModalDecidir
          id={abierta}
          onCerrar={() => setAbierta(null)}
          onDecidida={() => { setAbierta(null); cargar(cual); }}
        />
      )}
    </div>
  );
}


// =====================================================================
// LO QUE SE FIRMA, A LA VISTA ANTES DE FIRMARLO
// =====================================================================
// SE ENSEÑA LA PETICION ENTERA, campo por campo, y no solo la frase.
// Quien firma responde de lo que firma, y una frase resumida es
// suficiente para reconocer una solicitud pero no para responder de
// ella.
function ModalDecidir({ id, onCerrar, onDecidida }) {
  const [sol, setSol] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [rechazando, setRechazando] = useState(false);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get(`/autorizaciones/${id}`)
      .then((d) => vigente && setSol(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [id]);

  async function decidir(aprobar) {
    if (enviando) return;
    if (!aprobar && motivo.trim().length < 4) {
      setRechazando(true);
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post(`/autorizaciones/${id}/decidir`,
        { aprobar, motivo: aprobar ? null : motivo.trim() });
      // EL MENSAJE DEL SERVIDOR, TAL CUAL Y EN LA PANTALLA. Es él quien
      // sabe si se ejecutó, si faltan firmas o si falló al aplicarse, y
      // componer aquí un «listo» sería decir que algo se hizo cuando no
      // se hizo.
      //
      // Y SE QUEDA A LA VISTA hasta que la persona cierra: si la ventana
      // se cerrara sola, quien firmó lo segundo de dos no vería que
      // todavía falta una firma, y se iría creyendo que ya está.
      setResultado(r);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo decidir.');
      setEnviando(false);
    }
  }

  if (resultado) {
    const bien = ['EJECUTADA', 'APROBADA', 'PENDIENTE'].includes(resultado.estado);
    return (
      <Modal titulo="Listo" onCerrar={onDecidida}>
        <div className="space-y-4">
          <p className={`text-sm leading-relaxed rounded-lg p-3 border ${
            resultado.estado === 'FALLIDA'
              ? 'text-red-800 bg-red-50 border-red-200'
              : bien ? 'text-gray-800 bg-gray-50 border-gray-200'
                     : 'text-gray-800 bg-gray-50 border-gray-200'}`}>
            {resultado.mensaje}
          </p>
          <button
            type="button" onClick={onDecidida}
            className="w-full bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm"
          >
            Entendido
          </button>
        </div>
      </Modal>
    );
  }

  if (!sol) {
    return (
      <Modal titulo="Autorización" onCerrar={onCerrar}>
        {error ? <p className="text-sm text-red-700">{error}</p>
               : <Cargando filas={4} />}
      </Modal>
    );
  }

  return (
    <Modal titulo={sol.nombre_accion} onCerrar={onCerrar}>
      <div className="space-y-4">
        <p className="text-base font-semibold text-gray-900">{sol.resumen}</p>

        <dl className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
          <Fila campo="Lo pidió" valor={sol.solicitante} />
          <Fila campo="Estado" valor={<ChipEstado mapa={ESTADO_SOLICITUD} clave={sol.estado_visible} />} />
          <Fila campo="Firmas" valor={`${sol.firmas} de ${sol.aprobadores_requeridos}`} />
          {sol.estado_visible === 'PENDIENTE' && (
            <Fila campo="Plazo" valor={<TiempoRelativo valor={sol.caduca_en} />} />
          )}
          {sol.error_ejecucion && (
            <Fila campo="Por qué falló" valor={<span className="text-red-700">{sol.error_ejecucion}</span>} />
          )}
        </dl>

        {/* LA PETICION CONGELADA, ENTERA. Esto es lo que se va a
            ejecutar tal cual: ni una coma más ni una menos. */}
        <details className="rounded-lg border border-gray-200 p-3">
          <summary className="text-sm font-semibold text-gray-900 cursor-pointer">
            Exactamente qué se pidió
          </summary>
          <dl className="mt-2 text-sm space-y-1">
            {Object.entries(sol.carga || {}).map(([k, v]) => (
              <Fila key={k} campo={etiquetaCampo(k)} valor={String(v)} />
            ))}
          </dl>
          <p className="mt-2 text-xs text-gray-500">
            Lo que se apruebe se aplica <strong>así</strong>. Nadie lo
            vuelve a teclear ni lo puede cambiar entre que se pidió y se
            firma.
          </p>
        </details>

        {sol.decisiones?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Quién ha decidido</p>
            <ul className="text-sm space-y-1">
              {sol.decisiones.map((d, i) => (
                <li key={i} className="text-gray-700">
                  <strong>{d.quien}</strong>{' '}
                  {d.decision === 'APROBAR' ? 'firmó' : 'rechazó'}
                  {d.motivo && <> — «{d.motivo}»</>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!sol.se_ejecuta_sola && sol.estado_visible === 'PENDIENTE' && (
          /* NO SE PROMETE LO QUE NO SE HACE. De los nueve tipos, TEGO
             solo sabe ejecutar el ajuste de inventario. Un «aprobado»
             que la gente cree que ejecutó y no ejecutó es peor que no
             tener el control. */
          <AvisoNoSeEjecutaSola />
        )}

        {error && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}

        {sol.puedo_decidir ? (
          <div className="space-y-2">
            {rechazando && (
              <div>
                <label htmlFor="motivo-rechazo" className="block text-sm font-semibold text-gray-900 mb-1">
                  ¿Por qué la rechazas?
                </label>
                <input
                  id="motivo-rechazo" value={motivo} autoFocus
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Vuelve a contar el pasillo 3 primero"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Quien lo pidió va a leer esto. Sin un motivo, va a volver
                  a pedir exactamente lo mismo.
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button" disabled={enviando}
                onClick={() => decidir(false)}
                className="flex-1 border border-gray-300 text-gray-800 font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
              >
                Rechazar
              </button>
              <button
                type="button" disabled={enviando || rechazando}
                onClick={() => decidir(true)}
                className="flex-1 bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
              >
                {enviando ? 'Guardando…' : 'Autorizar'}
              </button>
            </div>
          </div>
        ) : (
          /* UN BOTON GRIS SIN EXPLICACION es lo que hace que alguien
             llame por teléfono para preguntar por qué no puede aprobar. */
          sol.por_que_no && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-2.5">
              {sol.por_que_no}
            </p>
          )
        )}
      </div>
    </Modal>
  );
}

function Fila({ campo, valor }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500 capitalize">{campo}</dt>
      <dd className="text-gray-900 text-right">{valor}</dd>
    </div>
  );
}
